import {
  useRef,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from "react";
import styles from "./planV2.module.scss";
import {
  UnstyledPackageTitle,
  UnstyledPlanDetailSummary,
} from "../../planDetailV2";
import { ColorType } from "../../../theme";
import classNames from "classnames";

const Base = (props: PropsWithChildren<{ bgColor: string }>): ReactElement => {
  return (
    <div className={styles.base} style={{ backgroundColor: props.bgColor }}>
      {props.children}
    </div>
  );
};

const DiagonalBadge = (
  props: PropsWithChildren<{ bgColor: string }>,
): ReactElement => {
  return (
    <div className={styles.diagonalBadgeContainer}>
      <div
        className={styles.diagonalBadge}
        style={{ backgroundColor: props.bgColor }}
      >
        {props.children}
      </div>
    </div>
  );
};

const Content = (props: PropsWithChildren): ReactElement => {
  return <div className={styles.content}>{props.children}</div>;
};

const PackegeInfo = (
  props: PropsWithChildren<{
    badgeText: string;
    title: string;
    rating: string;
    reviewText: string;
    theme: string;
  }>,
): ReactElement => {
  return (
    <UnstyledPackageTitle
      title={props.title}
      badgeText={props.badgeText}
      rating={props.rating}
      reviewLabel={props.reviewText}
      className={styles.packageInfo}
      theme={props.theme}
    />
  );
};

const PricingInfo = (
  props: PropsWithChildren<{
    fullPrice: string;
    discountBadge?: string;
    monthlyPrice: string;
    period: string;
    highlightText: string;
    theme: ColorType;
  }>,
): ReactElement => {
  return (
    <div className={styles.pricingInfo}>
      {props.fullPrice && props.discountBadge && (
        <div className={styles.fullPriceAndDiscount}>
          {props.fullPrice && (
            <p className={styles.fullPrice}>{props.fullPrice}</p>
          )}
          {props.discountBadge && (
            <div
              className={styles.discountBadge}
              style={{ backgroundColor: props.theme[50] }}
            >
              <p
                className={styles.discountLabel}
                style={{ color: props.theme[600] }}
              >
                {props.discountBadge}
              </p>
            </div>
          )}
        </div>
      )}
      <div className={styles.priceAndPeriod}>
        <p className={styles.monthlyPrice}>{props.monthlyPrice}</p>
        <p className={styles.period}>{props.period}</p>
      </div>
      {props.highlightText && (
        <div
          className={styles.highlightText}
          dangerouslySetInnerHTML={{ __html: props.highlightText }}
        ></div>
      )}
    </div>
  );
};

const CtaWrapper = (props: PropsWithChildren): ReactElement => {
  return <div className={styles.ctaWrapper}>{props.children}</div>;
};

const CtaButton = (props: {
  href: string;
  label: string;
  type: string;
  color: string;
  onClick?: (e: any) => void;
}): ReactElement => {
  return (
    <a
      href={props.href}
      className={classNames(styles.ctaButton, {
        [styles.blue]: props.color === "blue",
        [styles.pink]: props.color === "pink",
        [styles.purple]: props.color === "purple",
        [styles.primaryButton]: props.type === "primary",
        [styles.secondaryButton]: props.type === "secondary",
      })}
      onClick={props.onClick}
    >
      {props.label}
    </a>
  );
};

const SummaryContainer = (props: PropsWithChildren): ReactElement => {
  return (
    <UnstyledPlanDetailSummary.ItemList className={styles.summary}>
      {props.children}
    </UnstyledPlanDetailSummary.ItemList>
  );
};

const SummaryTitle = (props: { name: string }): ReactElement => {
  return (
    <UnstyledPlanDetailSummary.Title>
      {props.name}
    </UnstyledPlanDetailSummary.Title>
  );
};

const SummaryItem = (props: {
  text: string;
  iconColor: string;
  inactive?: boolean;
}): ReactElement => {
  return (
    <UnstyledPlanDetailSummary.Item
      iconColor={props.iconColor}
      inactive={props.inactive}
    >
      {props.text}
    </UnstyledPlanDetailSummary.Item>
  );
};

const PlayButton = (props: any) => {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48ZM20.25 32.8033L33.75 25.2576C34.75 24.6987 34.75 23.3013 33.75 22.7424L20.25 15.1967C19.25 14.6378 18 15.3365 18 16.4543V31.5457C18 32.6635 19.25 33.3622 20.25 32.8033Z"
        fill="white"
      />
    </svg>
  );
};

const Video = (props: { videoUrl: string; posterUrl: string }) => {
  const videoPlayerRef = useRef<any>();
  const [startedPlaying, setStartedPlaying] = useState(false);

  return (
    <div className={styles.videoContainer}>
      <video
        className={styles.video}
        src={props.videoUrl}
        poster={props.posterUrl}
        ref={videoPlayerRef}
        controls={false}
        onClick={() => {
          videoPlayerRef.current.play();
          setStartedPlaying(true);
        }}
      />

      {!startedPlaying && <PlayButton className={styles.playButton} />}
    </div>
  );
};

export const PlanV2 = {
  Base,
  DiagonalBadge,
  Content,
  PackegeInfo,
  PricingInfo,
  CtaWrapper,
  CtaButton,
  SummaryContainer,
  SummaryTitle,
  SummaryItem,
  Video,
};
